import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  TextField,
  Button,
  Paper,
  Divider,
  Stack
} from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
  Groups,
  Person,
  Search,
  RestartAlt,
  UnfoldMore,
  UnfoldLess,
  Email,
  PhoneAndroid,
  AssignmentInd,
  AdminPanelSettings
} from '@mui/icons-material';
import './TdxOrgChart.css';

// 컴포넌트 외부로 유틸리티 함수 이동 (ESLint 경고 해결)
const getAllDeptCodes = (nodes) => {
  let codes = [];
  nodes.forEach(node => {
    if (!node.isUser) {
      codes.push(node.dept_code);
      if (node.children?.length > 0) {
        codes = [...codes, ...getAllDeptCodes(node.children)];
      }
    }
  });
  return codes;
};

const buildDeptTree = (depts) => {
  const map = {};
  const tree = [];

  // 1. 모든 부서 노드 맵 생성
  depts.forEach(dept => {
    map[dept.dept_code] = { ...dept, children: [] };
  });

  // 2. 부서에 소속된 사용자(개인) 먼저 추가
  depts.forEach(dept => {
    const deptNode = map[dept.dept_code];
    if (dept.users && dept.users.length > 0) {
      dept.users.forEach(user => {
        deptNode.children.push({
          isUser: true,
          userid: user.userid,
          name: user.name,
          role: user.role,
          phone: user.phone || '',
          email: user.email || '',
          position: user.position || '',
          photo_path: user.photo_path || '',
          dept_name: deptNode.dept_name,
        });
      });
    }
  });

  // 3. 부서 계층 구조 연결
  depts.forEach(dept => {
    if (dept.parent_dept_code && map[dept.parent_dept_code]) {
      map[dept.parent_dept_code].children.push(map[dept.dept_code]);
    } else {
      tree.push(map[dept.dept_code]);
    }
  });

  // 4. 모든 레벨에서 개인이 부서보다 위에 오도록 강제 정렬 (최종 보장)
  const sortRecursive = (nodes) => {
    nodes.sort((a, b) => {
      if (a.isUser && !b.isUser) return -1;
      if (!a.isUser && b.isUser) return 1;
      return 0;
    });
    nodes.forEach(node => {
      if (node.children?.length > 0) {
        sortRecursive(node.children);
      }
    });
  };

  sortRecursive(tree);

  return tree;
};

const filterTree = (nodes, term) => {
  if (!term) return nodes;
  return nodes
    .map(node => {
      if (node.isUser) {
        if (node.name.toLowerCase().includes(term.toLowerCase())) return node;
        return null;
      } else {
        const matchedDept = node.dept_name.toLowerCase().includes(term.toLowerCase());
        const filteredChildren = filterTree(node.children || [], term);
        if (matchedDept || filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }
        return null;
      }
    })
    .filter(Boolean);
};

function OrgChart() {
  const [deptTree, setDeptTree] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    axios.get('/api/orgchart')
      .then(res => {
        const tree = buildDeptTree(res.data);
        setDeptTree(tree);
        const allDeptCodes = getAllDeptCodes(tree);
        setExpandedItems(allDeptCodes);
      })
      .catch(err => {
        console.error('조직도 불러오기 실패:', err);
        alert('조직 정보를 불러올 수 없습니다');
      });
  }, []);


  const toggleAll = (expandAll) => {
    if (expandAll) {
      setExpandedItems(getAllDeptCodes(deptTree));
    } else {
      setExpandedItems([]);
    }
  };

  const filteredTree = filterTree(deptTree, searchTerm);

  const renderMuiTree = (nodes) => (
    nodes.map(node => {
      const id = node.isUser ? `user-${node.userid}` : node.dept_code;
      return (
        <TreeItem
          key={id}
          itemId={id}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
              {node.isUser ? (
                <Person sx={{ mr: 1, fontSize: '1.1rem', color: '#1976d2' }} />
              ) : (
                <Groups
                  sx={{ mr: 1, fontSize: '1.2rem', color: '#ffa000', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const isExpanded = expandedItems.includes(node.dept_code);
                    const newExpanded = isExpanded
                      ? expandedItems.filter(id => id !== node.dept_code)
                      : [...expandedItems, node.dept_code];
                    setExpandedItems(newExpanded);
                  }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: node.isUser ? 400 : 600,
                  whiteSpace: 'nowrap' // 텍스트 줄바꿈 방지
                }}
              >
                {node.isUser ? `${node.name} ${node.position}` : node.dept_name}
              </Typography>
            </Box>
          }
          onClick={() => node.isUser && setSelectedUser(node)}
        >
          {node.children?.length > 0 && renderMuiTree(node.children)}
        </TreeItem>
      );
    })
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* 헤더 섹션 */}
      <Paper elevation={0} sx={{ mb: 4, p: 3, borderRadius: 2, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', mb: 1 }}>
          <Groups sx={{ mr: 1 }} /> 조직도
        </Typography>
        <Typography variant="body2" color="text.secondary">
          부서별 구성원과 구조를 한눈에 확인하세요
        </Typography>
      </Paper>

      {/* 트리와 상세 정보를 감싸는 전체 테두리 박스 (높이 고정) */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3,
          bgcolor: '#fafbfc',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid #e0e4e8',
          height: 585, // 전체 높이 고정
          display: 'flex'
        }}
      >
        <Grid container spacing={3} sx={{ height: '100%' }}>
          {/* 왼쪽: 검색 및 트리 */}
          <Grid item xs={12} md={5} sx={{ height: '100%' }}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', border: 'none', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 2 }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="이름 또는 부서명 검색"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                        }}
                      />
                      <Button
                        variant="outlined"
                        startIcon={<RestartAlt />}
                        onClick={() => setSearchTerm('')}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        초기화
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="text" startIcon={<UnfoldMore />} onClick={() => toggleAll(true)}>전체 펼치기</Button>
                      <Button size="small" variant="text" startIcon={<UnfoldLess />} onClick={() => toggleAll(false)}>전체 접기</Button>
                    </Box>
                    <Divider sx={{ mt: 1 }} />
                  </Box>

                  {/* 트리 스크롤 영역 */}
                  <Box sx={{ flexGrow: 1, overflow: 'auto', pr: 1 }}>
                    <SimpleTreeView
                      expandedItems={expandedItems}
                      onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
                    >
                      {renderMuiTree(filteredTree)}
                    </SimpleTreeView>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* 오른쪽: 상세 정보 */}
          <Grid item xs={12} md={7} sx={{ height: '100%', overflow: 'hidden' }}>
            {selectedUser ? (
              <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #f0f0f0', bgcolor: '#fff', width: 550 }}>
                <Box sx={{ height: 6, bgcolor: '#1976d2', position: 'sticky', top: 0, zIndex: 1 }} />
                <CardContent sx={{ p: 2 }}> {/* 패딩 축소 */}
                  <Stack spacing={1.5}> {/* 전체 간격 축소 */}
                    <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', py: 1.25 }}> {/* 상하 여백 10px 추가 */}
                      <Box sx={{ width: 100 }}> {/* 아바타 크기 100px로 복구 */}
                        <Avatar
                          src={selectedUser.photo_path ? `${selectedUser.photo_path}?${new Date().getTime()}` : ''}
                          sx={{ width: 100, height: 100, boxShadow: 3 }}
                        >
                          <Person sx={{ fontSize: 50 }} />
                        </Avatar>
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 0.2 }}>
                          {selectedUser.name}
                        </Typography>
                        <Typography color="text.secondary" variant="body2" sx={{ mb: 0.2 }}>
                          {selectedUser.position || '직책 없음'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500 }}>
                          {selectedUser.dept_name}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 0.5 }} /> {/* 구분선 간격 최소화 */}

                    <Stack spacing={2.7} sx={{ pl: 0 }}> {/* 간격을 7px 더 추가하여 시원하게 배치 */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 100, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AssignmentInd fontSize="small" sx={{ color: '#aaa', fontSize: '1rem' }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#777' }}>아이디</Typography>
                        </Box>
                        <Box sx={{ width: 300 }}>
                          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>{selectedUser.userid}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 100, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AdminPanelSettings fontSize="small" sx={{ color: '#aaa', fontSize: '1rem' }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#777' }}>권한</Typography>
                        </Box>
                        <Box sx={{ width: 300 }}>
                          <Typography variant="body2" sx={{ color: '#333' }}>{selectedUser.role}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 100, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email fontSize="small" sx={{ color: '#aaa', fontSize: '1rem' }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#777' }}>이메일</Typography>
                        </Box>
                        <Box sx={{ width: 300 }}>
                          <Typography variant="body2" sx={{ color: '#333' }}>{selectedUser.email || '-'}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 100, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneAndroid fontSize="small" sx={{ color: '#aaa', fontSize: '1rem' }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#777' }}>연락처</Typography>
                        </Box>
                        <Box sx={{ width: 300 }}>
                          <Typography variant="body2" sx={{ color: '#333' }}>{selectedUser.phone || '-'}</Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fcfcfc',
                  borderStyle: 'dashed',
                  borderColor: '#e0e0e0',
                  borderRadius: 2,
                  minHeight: 170, // 사용자님이 지정하신 170px로 조정
                  px: 8,
                  textAlign: 'center'
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Box component="span" sx={{ fontSize: '1.2rem' }}>👈</Box>
                  조직도에서 회원을 클릭하면 상세 정보가 표시됩니다
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default OrgChart;